"use client";

import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";
import { LtrToken } from "@/components/cdm/LtrToken";
import { advanceScreen } from "@/lib/presenter/advanceScreen";

/**
 * Screen 4.1 — Welcome-back confirmation, with Bridging Beat A folded in.
 * Localised via next-intl; mixed-script tokens (OrbIDs) stay LTR via
 * <LtrToken> so they read correctly under RTL Arabic.
 */
export function WelcomeBack({ className }: { className?: string }) {
  const tBridge = useTranslations("bridge");
  const tWB     = useTranslations("welcomeBack");
  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="glass relative overflow-hidden p-5">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-state-traced to-transparent" />

        <div className="flex items-center gap-3">
          <span className="rounded-full border border-state-traced/40 bg-state-traced/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-state-traced">
            {tBridge("aBadge")}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-orbitra-muted">
            {tBridge("aSubtag")}
          </span>
        </div>

        <h2 className="mt-3 font-display text-2xl font-semibold text-orbitra-paper">
          {tBridge("aHeadline")}
        </h2>
        <p className="mt-1 text-[13px] text-state-traced">{tBridge("aSubcopy")}</p>
      </div>

      <div className="grid flex-1 grid-cols-3 gap-4">
        <div className="glass col-span-2 flex flex-col p-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{tWB("tradePipelineReturning")}</p>
          <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">{tWB("headline")}</h3>
          <p className="mt-1 text-[12px] text-orbitra-teal-soft">{tWB("lastTrade")}</p>

          <div className="mt-5 grid grid-cols-2 gap-3 text-[12px]">
            <Field label={tWB("fieldExistingOrbid")}>
              <LtrToken>orbid:5493-almadar-ae</LtrToken>
            </Field>
            <Field label={tWB("fieldVerifiedBadge")}>{tWB("fieldVerifiedBadgeValue")}</Field>
            <Field label={tWB("fieldLastCounterparty")}>{tWB("auroraAvesBrasilSA")}</Field>
            <Field label={tWB("fieldReputation")}>{tWB("fieldReputationValue")}</Field>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2 text-[10px]">
            <Skipped label={tWB("skippedOnboarding")} tag={tWB("skippedTag")} />
            <Skipped label={tWB("skippedFee")}        tag={tWB("skippedTag")} />
            <Skipped label={tWB("skippedVerifCall")}  tag={tWB("skippedTag")} />
          </div>

          <div className="mt-auto pt-5">
            <button type="button" onClick={advanceScreen} className="cta-primary px-4 py-2.5 text-[12px]">
              {tWB("submitCta")}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="glass p-4 text-[11px]">
            <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-state-verified">{tWB("echoAgentLabel")}</p>
            <p className="mt-2 italic text-orbitra-paper">"{tWB("echoCameo")}"</p>
          </div>
          <div className="glass p-4 text-[11px]">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{tWB("whatPersistsHeader")}</p>
            <ul className="mt-2 space-y-1 text-orbitra-teal-soft">
              <li>· <LtrToken mono={false}>OrbID</LtrToken> {tWB("persistsOrbIdRest")}</li>
              <li>{tWB("persistsKyb")}</li>
              <li>{tWB("persistsLiveVerif")}</li>
              <li>{tWB("persistsTraderBadge")}</li>
              <li>{tWB("persistsCounterparty")}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className="mt-0.5 text-orbitra-paper">{children}</p>
    </div>
  );
}

function Skipped({ label, tag }: { label: string; tag: string }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-orbitra-muted/30 bg-orbitra-ink/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-orbitra-muted line-through">
      <span>{label}</span>
      <span className="not-italic no-underline">{tag}</span>
    </div>
  );
}
